import { CountryCode } from 'libphonenumber-js/min';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLoginCountries, LoginCountry } from '../utils/loginIdentifier';
import 'flag-icons/css/flag-icons.min.css';
import './country-code-select.scss';

const Flag: React.FC<{ code: CountryCode }> = ({ code }) => (
  <span
    className={`country-code-flag fi fi-${code.toLowerCase()}`}
    aria-hidden='true'
  />
);

interface CountryCodeSelectProps {
  value: CountryCode;
  onChange: (country: CountryCode) => void;
  disabled?: boolean;
}

const matches = (country: LoginCountry, query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    country.name.toLowerCase().includes(q) ||
    country.code.toLowerCase() === q ||
    country.callingCode.includes(q.startsWith('+') ? q : `+${q}`)
  );
};

/**
 * Flag + dialling-code picker that sits in front of the login phone field.
 * Button opens a searchable listbox (type a country name, ISO code or "+44").
 */
const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const countries = useMemo(getLoginCountries, []);
  const selected = countries.find((c) => c.code === value) ?? countries[0];
  const filtered = useMemo(
    () => countries.filter((c) => matches(c, query)),
    [countries, query],
  );

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(Math.max(0, countries.indexOf(selected)));
    searchRef.current?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, countries, selected]);

  useEffect(() => {
    const active = filtered[activeIndex];
    if (!active) return;
    document
      .getElementById(`${listId}-${active.code}`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, filtered, listId]);

  const choose = (country: LoginCountry) => {
    onChange(country.code);
    setOpen(false);
  };

  const onSearchKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
        event.preventDefault();
        if (filtered[activeIndex]) choose(filtered[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      default:
    }
  };

  return (
    <div className='country-code-select' ref={rootRef}>
      <button
        type='button'
        className='country-code-button'
        aria-label={`${t('login.countryLabel')}: ${selected.name} ${selected.callingCode}`}
        aria-haspopup='listbox'
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <Flag code={selected.code} />
        <span className='country-code-dial'>{selected.callingCode}</span>
        <svg
          className='country-code-chevron'
          width='12'
          height='12'
          viewBox='0 0 12 12'
          aria-hidden='true'
        >
          <path
            d='M2.5 4.5L6 8l3.5-3.5'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>
      {open && (
        <div className='country-code-popover'>
          <input
            ref={searchRef}
            className='country-code-search'
            type='text'
            role='combobox'
            aria-controls={listId}
            aria-expanded='true'
            aria-autocomplete='list'
            aria-activedescendant={
              filtered[activeIndex]
                ? `${listId}-${filtered[activeIndex].code}`
                : undefined
            }
            placeholder={t('login.countrySearchPlaceholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onSearchKeyDown}
          />
          <ul
            id={listId}
            className='country-code-list'
            role='listbox'
            aria-label={t('login.countryLabel')}
          >
            {filtered.length === 0 && (
              <li className='country-code-empty'>
                {t('login.countryNoResults')}
              </li>
            )}
            {filtered.map((country, index) => (
              <li
                key={country.code}
                id={`${listId}-${country.code}`}
                role='option'
                aria-selected={country.code === selected.code}
                className={`country-code-option${
                  index === activeIndex ? ' country-code-option-active' : ''
                }`}
                onMouseMove={() => setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(country)}
              >
                <Flag code={country.code} />
                <span className='country-code-name'>{country.name}</span>
                <span className='country-code-dial'>{country.callingCode}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountryCodeSelect;
